import React, { useEffect, useState, useCallback } from "react";
import { ShieldAlert, Plus, X, Eye, CheckCircle2, Clock } from "lucide-react";
import { Card, Button, Badge, Spinner, Modal } from "../lib/ui";
import { PhotoEvidence } from "../components/PhotoEvidence";
import { useToast } from "../lib/toast";
import api from "../lib/api";

const FALLBACK_REASONS = ["Selisih uang kas", "Komplain pelanggan", "Printer/EDC error",
  "Mati listrik", "POS Popcorn error", "Salah input penjualan", "Lainnya"];
const isOpen = (s) => s === "PENDING" || s === "UNDER_REVIEW";
const fmt = (iso) => { try { return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const fileToDataUrl = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

export default function CashierIncident() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [reasons, setReasons] = useState(FALLBACK_REASONS);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category: "", note: "", photos: [] });
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/incidents", { params: { type_key: "CASHIER_INCIDENT" } });
      setRows(data);
    } catch { toast.error("Gagal memuat incident"); } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => {
    load();
    api.get("/incidents/types").then(({ data }) => {
      const t = (data || []).find((x) => x.key === "CASHIER_INCIDENT");
      if (t?.reasons?.length) setReasons(t.reasons);
    }).catch(() => {});
  }, [load]);

  const submit = async () => {
    if (!form.category) { toast.error("Pilih jenis incident"); return; }
    setSaving(true);
    try {
      await api.post("/incidents", {
        type_key: "CASHIER_INCIDENT", category: form.category,
        note: form.note || null, photos: form.photos,
      });
      toast.success("Incident dilaporkan");
      setOpen(false); setForm({ category: "", note: "", photos: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };

  const close = async (id) => {
    try { await api.post(`/incidents/${id}/close`); toast.success("Incident ditutup"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="cashier-incident-page">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-ink"><ShieldAlert size={22} className="text-primary" /> Incident</h1>
          <p className="text-sm text-ink-soft">Laporkan jika terjadi masalah / kejadian khusus.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="create-incident-btn"><Plus size={16} /> Buat Incident</Button>
      </div>

      <Card data-testid="incident-list">
        {loading ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-mute">Belum ada incident.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <tr><th className="py-2 pr-3">Waktu</th><th className="py-2 pr-3">Judul Incident</th><th className="py-2 pr-3 text-center">Status</th><th className="py-2 text-center">Aksi</th></tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((i) => (
                <tr key={i.id} data-testid={`incident-row-${i.id}`}>
                  <td className="py-2.5 pr-3 text-ink-soft">{fmt(i.created_at)}</td>
                  <td className="py-2.5 pr-3 font-medium text-ink">{i.category || i.type_name}</td>
                  <td className="py-2.5 pr-3 text-center">
                    <Badge tone={isOpen(i.status) ? "warn" : "success"}>
                      {isOpen(i.status) ? <><Clock size={11} /> Open</> : <><CheckCircle2 size={11} /> Closed</>}
                    </Badge>
                  </td>
                  <td className="py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setView(i)} className="rounded-lg p-1.5 text-ink-soft hover:bg-app" data-testid={`incident-view-${i.id}`}><Eye size={15} /></button>
                      {isOpen(i.status) && <button onClick={() => close(i.id)} className="rounded-lg px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50" data-testid={`incident-close-${i.id}`}>Tutup</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Create modal */}
      <Modal open={open} onClose={() => setOpen(false)} title="Buat Incident" size="md"
        footer={<Button onClick={submit} loading={saving} data-testid="incident-submit">Simpan Incident</Button>}>
        <div className="space-y-3" data-testid="incident-modal">
          <div>
            <label className="text-xs font-semibold text-ink-soft">Jenis / Judul Incident <span className="text-primary">*</span></label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="incident-type-select" className="mt-0.5 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary">
              <option value="">— Pilih —</option>
              {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-soft">Deskripsi</label>
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3}
              data-testid="incident-note" placeholder="Jelaskan kejadiannya..."
              className="mt-0.5 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <PhotoEvidence label="Foto (Opsional)" testid="incident-photo" max={3}
            tokens={form.photos} setTokens={(t) => setForm({ ...form, photos: t })} uploadFile={fileToDataUrl} />
        </div>
      </Modal>

      {/* View modal */}
      <Modal open={!!view} onClose={() => setView(null)} title={view?.category || "Incident"} size="md">
        {view && (
          <div className="space-y-3 text-sm" data-testid="incident-view-modal">
            <div className="flex items-center gap-2"><Badge tone={isOpen(view.status) ? "warn" : "success"}>{isOpen(view.status) ? "Open" : "Closed"}</Badge><span className="text-ink-mute">{fmt(view.created_at)}</span></div>
            <p className="text-ink">{view.note || <span className="text-ink-mute">Tanpa deskripsi.</span>}</p>
            {(view.photos || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {view.photos.map((p, i) => <img key={i} src={p} alt="" className="h-24 w-24 rounded-xl border border-line object-cover" />)}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
